import { useMemo } from "react";
import { format } from "date-fns";
import { convertToCurrency } from "creso-core/utils";
import type { RecentTransactionsReportTransaction } from "creso-core/types";
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableCell,
  TableRow,
  cn,
} from "web-design-system";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { OrderTypeBadge, ProductTypeBadge } from "web-core";
import Units from "@funds/components/atoms/units";
import { StickyNote } from "lucide-react";

type ReportColumnConfig<T> = Array<{
  name: string;
  render: (data: T) => React.ReactNode;
  className?: string;
}>;

export function OrdersReport({
  transactions,
  startDate,
  endDate,
}: {
  transactions: RecentTransactionsReportTransaction[];
  startDate?: string;
  endDate?: string;
}) {
  const transactionColumns: ReportColumnConfig<(typeof transactions)[number]> =
    useMemo(
      () => [
        {
          name: "Scheme name",
          render: (data) => (
            <div className="flex flex-row gap-2 items-center">
              <AmcLogo amc_code={data.amc_morningstar_code} />
              <div className="flex flex-col gap-1">
                <div className="flex flex-row items-center gap-2">
                  <span className="text-sm">{data.scheme_name}</span>
                  <ProductTypeBadge type={data.product_type} />
                </div>
                {data.folio_number ? (
                  <span className="text-xs text-muted-foreground">
                    Folio: {data.folio_number}
                  </span>
                ) : null}
              </div>
            </div>
          ),
        },
        {
          name: "Placed on",
          render: (data) => (
            <div className="flex flex-col gap-1">
              <span className="text-sm">
                {format(data.created_at, "dd MMM yyyy")}
              </span>
              <span className="text-xs text-muted-foreground">
                {format(data.created_at, "hh:mm a")}
              </span>
            </div>
          ),
          className: "w-55 print:w-40",
        },
        {
          name: "Order type",
          render: (data) => <OrderTypeBadge type={data.type} />,
          className: "w-55 print:w-40",
        },
        {
          name: "Amount",
          render: (data) => (
            <div className="flex flex-col gap-1">
              {data.amount ? (
                <span className="text-sm">
                  {convertToCurrency(data.amount)}
                </span>
              ) : (
                <span className="text-sm">-</span>
              )}
              {data.units ? (
                <span className="text-xs text-muted-foreground">
                  <Units>{data.units}</Units>
                </span>
              ) : null}
            </div>
          ),
          className: "w-55 print:w-40",
        },
        {
          name: "NAV",
          render: (data) => (
            <div className="flex flex-col gap-1">
              <span className="text-sm">
                {data.nav ? convertToCurrency(data.nav, 2) : "-"}
              </span>
              {data.nav_date ? (
                <span className="text-xs text-muted-foreground">
                  {format(data.nav_date, "dd MMM yyyy")}
                </span>
              ) : null}
            </div>
          ),
          className: "w-55 print:w-40",
        },
      ],
      []
    );

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <h4>Orders</h4>
        <div className="flex flex-col items-center justify-center gap-1 bg-muted border border-border/50 rounded-lg py-12 text-muted-foreground">
          <StickyNote className="size-12" strokeWidth={1} strokeDasharray={2} />
          <span className="mt-6">No transactions found</span>
          {startDate && endDate ? (
            <span>{`(${format(startDate, "dd MMM yyyy")} - ${format(
              endDate,
              "dd MMM yyyy"
            )})`}</span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h4>Orders</h4>
      <Table className="border border-border">
        <TableHeader className="bg-muted rounded-t-lg">
          <TableRow>
            {transactionColumns.map((column) => (
              <TableHead
                className={cn(
                  column.className,
                  "first:rounded-tl-lg last:rounded-tr-lg"
                )}
                key={column.name}
              >
                {column.name}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions.map((transaction) => (
            <TableRow key={transaction.id} className="print:break-inside-avoid">
              {transactionColumns.map((column) => (
                <TableCell className={column.className} key={column.name}>
                  {column.render(transaction)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
